import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import AvatarItem from '@atlaskit/avatar/avatar-item';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled - go/akcss
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled - go/akcss
import Flex from '@atlaskit/primitives/components/flex';

import { expVal } from '@confluence/feature-experiments';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader.ts';

import type { Account, KnownAccount } from '../../../hooks/index.ts';
import { AccountType } from '../../../hooks/index.ts';
import { getLastViewedText } from '../../analyticsUtils.ts';
import { AgentRowAvatar } from '../../common/AgentActivity/agentActivity.tsx';
import { AnalyticsProfileIdentity } from '../../common/AnalyticsProfileIdentity.tsx';
import {
	AvatarSkeleton,
	PrimaryTextSkeleton,
	SecondaryTextSkeleton,
} from '../LoadingSkeleton/index.ts';
import { EngagementCell } from '../../PageAnalytics/EngagementCell.tsx';

import { UserListRow } from './userListStyled.tsx';

const i18n = defineMessages({
	anonymous: {
		id: 'confluence-analytics.dialog.user-list.anonymous-user-name',
		defaultMessage: 'Anonymous',
		description: 'Text for an anonymous user in a list of users.',
	},
	deactivated: {
		id: 'confluence-analytics.dialog.user-list.deactivated-user-label',
		defaultMessage: 'Deactivated',
		description: 'Label indicating that a user is no longer active.',
	},
	agentAttribution: {
		id: 'confluence-analytics.dialog.user-list.agent-attribution',
		defaultMessage:
			'with {name}{count, plural, =0 {} one { and # other} other { and # others}} • {time}',
		description:
			'Secondary text under an AI agent viewer in the byline View tab, naming the person the agent acted on behalf of and when. {count} is the number of additional people beyond {name}: 0 renders "with Alex • 1 hour ago", 1 renders "with Alex and 1 other • 1 hour ago", 2+ renders "with Alex and 3 others • 1 hour ago". {time} is the relative last-viewed time.',
	},
});

/**
 * True when the account is an AI agent viewer (not anonymous). Data-only check;
 * the render behaviour it drives is gated separately behind ai_agent_analytics.
 */
const isAgentAccount = (account: Account): account is KnownAccount =>
	account.accountType !== AccountType.ANONYMOUS && account.isAgent === true;

const EngagementLozengeSkeletonStyles = xcss({
	width: '80px',
	height: '24px',
	backgroundColor: 'color.skeleton',
});

const EngagementLozengeStyles = xcss({
	width: '120px',
	textAlign: 'right',
});

const GuestAvatarStyles = xcss({
	width: '200px',
});

const DefaultAvatarStyles = xcss({
	width: '260px',
});

const UserContainerStyles = xcss({
	marginLeft: 'space.negative.075',
});

export type AnalyticsDialogUserListRowProps =
	| {
			isLoading: true;
			account?: undefined;
	  }
	| {
			isLoading: false;
			account: Account;
	  };

export const AnalyticsDialogUserListRow = ({
	isLoading,
	account,
}: AnalyticsDialogUserListRowProps): React.JSX.Element => {
	const intl = useIntl();

	const displayName = useMemo(() => {
		if (isLoading) {
			return null;
		}
		if (account.accountType === AccountType.ANONYMOUS) {
			return intl.formatMessage(i18n.anonymous);
		}
		if (isAgentAccount(account) && expVal('ai_agent_analytics', 'isEnabled', false)) {
			// Agent name is resolved upstream by the shared classification core
			// (real name, "Rovo", or "Unknown agents") — never show "(Deactivated)".
			return account.name;
		}
		if (account.isDeactivated) {
			return `${account.name} (${intl.formatMessage(i18n.deactivated)})`;
		}
		return account.name;
	}, [account, intl, isLoading]);

	const secondaryText = useMemo(() => {
		if (isLoading) {
			return null;
		}
		if (!account?.lastViewedAt) {
			return null;
		}
		const time = getLastViewedText(new Date(account.lastViewedAt), intl);
		const namedAttributedUsers =
			account.accountType !== AccountType.ANONYMOUS
				? account.attributedUsers?.filter((attributedUser) => attributedUser.name)
				: undefined;
		// Agent attribution: when the agent acted on behalf of one or more people,
		// prefix the time with "with {name}[ and N others]". Autonomous agents (no
		// attributed users) and humans keep the plain relative time.
		if (
			time &&
			isAgentAccount(account) &&
			namedAttributedUsers &&
			namedAttributedUsers.length > 0 &&
			expVal('ai_agent_analytics', 'isEnabled', false)
		) {
			return intl.formatMessage(i18n.agentAttribution, {
				name: namedAttributedUsers[0].name,
				count: namedAttributedUsers.length - 1,
				time,
			});
		}
		return time;
	}, [account, intl, isLoading]);

	const engagementLozenge = useMemo(() => {
		if (isLoading) {
			return null;
		}
		// Agents have no engagement signal — leave the cell empty for agent rows.
		if (isAgentAccount(account) && expVal('ai_agent_analytics', 'isEnabled', false)) {
			return null;
		}
		return (
			<EngagementCell isEngaged={account?.isEngaged ?? null} lastViewDate={account.lastViewedAt} />
		);
	}, [account, isLoading]);

	const ariaLabel = useMemo(() => {
		const ariaLabelParts = [displayName, secondaryText].filter(Boolean);
		if (ariaLabelParts.length === 0) {
			return undefined;
		}
		return ariaLabelParts.join(', ');
	}, [displayName, secondaryText]);
	const name = displayName ?? '';
	const isGuest = account?.accountType === AccountType.GUEST;

	const avatarItem = useMemo(() => {
		let avatar: React.ReactNode;
		if (isLoading) {
			avatar = <AvatarSkeleton />;
		} else if (isAgentAccount(account) && expVal('ai_agent_analytics', 'isEnabled', false)) {
			avatar = (
				<AgentRowAvatar
					name={name}
					agentIcon={account.agentIcon}
					avatarUrl={account.avatarURL || undefined}
				/>
			);
		} else {
			avatar = (
				<Avatar
					src={
						account.accountType === AccountType.ANONYMOUS
							? undefined
							: account.avatarURL || undefined
					}
					name={name}
					size="small"
				/>
			);
		}
		return (
			<Box
				xcss={isGuest ? GuestAvatarStyles : DefaultAvatarStyles}
				testId="analytics-dialog-user-list-row-text"
			>
				<AvatarItem
					avatar={avatar}
					primaryText={isLoading ? <PrimaryTextSkeleton /> : displayName}
					secondaryText={isLoading ? <SecondaryTextSkeleton /> : secondaryText}
					isTruncationDisabled={false}
				/>
			</Box>
		);
	}, [isLoading, account, name, secondaryText, isGuest, displayName]);

	let identity = avatarItem;
	if (!isLoading && expVal('ai_agent_analytics_cards', 'isEnabled', false)) {
		if (isAgentAccount(account) && expVal('ai_agent_analytics', 'isEnabled', false)) {
			identity = (
				<AnalyticsProfileIdentity
					userId={account.identityAccountId ?? account.id}
					fullName={name}
					agent={{}}
				>
					{avatarItem}
				</AnalyticsProfileIdentity>
			);
		} else {
			identity = (
				<AnalyticsProfileIdentity
					userId={account.accountType === AccountType.ANONYMOUS ? undefined : account.id}
					fullName={name}
				>
					{avatarItem}
				</AnalyticsProfileIdentity>
			);
		}
	}

	return (
		<UserListRow aria-label={ariaLabel}>
			<Flex
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				xcss={UserContainerStyles}
			>
				{identity}
				{isGuest && <ExternalCollaboratorUserLozenge />}
				{isLoading ? (
					<Box
						xcss={EngagementLozengeSkeletonStyles}
						testId="analytics-dialog-engagement-lozenge-loading"
					/>
				) : (
					<Box xcss={EngagementLozengeStyles}>{engagementLozenge}</Box>
				)}
			</Flex>
		</UserListRow>
	);
};
